import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { KeyRound, Loader2, Mail } from "lucide-react";

import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

const field = "surface-input w-full px-3 py-2 text-sm";

function errText(t: (k: string) => string, e: any) {
    const d = e?.response?.data?.detail;
    return typeof d === "string" ? t(`account.err.${d}`) : formatApiError(e);
}

function PasswordForm() {
    const { t } = useTranslation();
    const [cur, setCur] = useState(""); const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
    const [busy, setBusy] = useState(false);
    const ok = cur && pw.length >= 12 && pw === pw2;
    const submit = async (e: React.FormEvent) => {
        e.preventDefault(); setBusy(true);
        try {
            await api.post("/auth/change-password", { current_password: cur, new_password: pw });
            toast.success(t("account.pw.saved")); setCur(""); setPw(""); setPw2("");
        } catch (err: any) { toast.error(errText(t, err)); } finally { setBusy(false); }
    };
    return (
        <form onSubmit={submit} className="surface-card p-5 space-y-3" data-testid="account-password-form">
            <div className="flex items-center gap-2 font-medium" style={{ color: "var(--text)" }}><KeyRound size={16} /> {t("account.pw.title")}</div>
            <input type="password" autoComplete="current-password" value={cur} onChange={e => setCur(e.target.value)} placeholder={t("account.current_password")} className={field} data-testid="account-pw-current" />
            <input type="password" autoComplete="new-password" value={pw} onChange={e => setPw(e.target.value)} placeholder={t("account.pw.new")} className={field} data-testid="account-pw-new" />
            <input type="password" autoComplete="new-password" value={pw2} onChange={e => setPw2(e.target.value)} placeholder={t("account.pw.repeat")} className={field} data-testid="account-pw-repeat" />
            <div className="text-xs" style={{ color: pw && pw.length < 12 ? "var(--error-fg)" : "var(--text-muted)" }}>{t("account.pw.hint")}</div>
            {pw2 && pw !== pw2 && <div className="text-xs" style={{ color: "var(--error-fg)" }} data-testid="account-pw-mismatch">{t("account.pw.mismatch")}</div>}
            <button disabled={!ok || busy} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50" style={{ background: "var(--primary)", color: "var(--primary-fg)" }} data-testid="account-pw-submit">
                {busy && <Loader2 size={14} className="animate-spin" />} {t("account.pw.submit")}
            </button>
        </form>
    );
}

function EmailForm() {
    const { t } = useTranslation();
    const { me, refresh } = useAuth() as any;
    const [email, setEmail] = useState(""); const [cur, setCur] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (e: React.FormEvent) => {
        e.preventDefault(); setBusy(true);
        try {
            await api.post("/auth/change-email", { current_password: cur, new_email: email });
            toast.success(t("account.email.saved")); setEmail(""); setCur("");
            if (typeof refresh === "function") await refresh();
        } catch (err: any) { toast.error(errText(t, err)); } finally { setBusy(false); }
    };
    return (
        <form onSubmit={submit} className="surface-card p-5 space-y-3" data-testid="account-email-form">
            <div className="flex items-center gap-2 font-medium" style={{ color: "var(--text)" }}><Mail size={16} /> {t("account.email.title")}</div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }} data-testid="account-email-current">{t("account.email.current")}: <b>{me?.user?.email}</b></div>
            <input type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t("account.email.new")} className={field} data-testid="account-email-new" />
            <input type="password" autoComplete="current-password" value={cur} onChange={e => setCur(e.target.value)} placeholder={t("account.current_password")} className={field} data-testid="account-email-current-pw" />
            <button disabled={!cur || !email.includes("@") || busy} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50" style={{ background: "var(--primary)", color: "var(--primary-fg)" }} data-testid="account-email-submit">
                {busy && <Loader2 size={14} className="animate-spin" />} {t("account.email.submit")}
            </button>
        </form>
    );
}

export default function Account() {
    const { t } = useTranslation();
    return (
        <div className="space-y-5 max-w-xl" data-testid="account-page">
            <h1 className="text-2xl font-semibold" style={{ color: "var(--text)" }}>{t("account.title")}</h1>
            <EmailForm />
            <PasswordForm />
        </div>
    );
}
